import rtlPlugin from 'stylis-plugin-rtl';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import {
  buttonClasses,
  createTheme,
  Palette,
  PaletteOptions,
  accordionClasses,
} from '@mui/material';
import { PropsWithChildren } from 'react';
import { TypographyOptions } from '@mui/material/styles/createTypography';
import createPalette from '@mui/material/styles/createPalette';
import { prefixer } from 'stylis';
import { AccordionArrow } from 'Components';
export const cacheRtl = createCache({
  key: 'muirtl',
  stylisPlugins: [prefixer, rtlPlugin],
  prepend: true,
});
const defaultTheme = createTheme();

const typography:
  | TypographyOptions
  | ((palette: Palette) => TypographyOptions)
  | undefined = {
  fontFamily: "'Open Sans', sans-serif",
  h1: {
    fontStyle: 'normal',
    fontSize: '18px',
    lineHeight: '28px',
    [defaultTheme.breakpoints.up('sm')]: {
      lineHeight: '40px',
      fontSize: '28px',
    },
  },
  h2: {
    fontStyle: 'normal',
    fontWeight: 'normal',
    fontSize: '20px',
    lineHeight: '16px',
    [defaultTheme.breakpoints.up('sm')]: {
      fontStyle: 'normal',
      fontSize: '22px',
      lineHeight: '32px',
    },
  },
  h3: {
    fontSize: '16px',
    fontStyle: 'normal',
    lineHeight: '22px',
    [defaultTheme.breakpoints.up('sm')]: {
      fontSize: '18px',
      lineHeight: '32px',
    },
  },
  body1: {
    fontStyle: 'normal',
    fontSize: '16px',
    lineHeight: '24px',
  },
  subtitle1: {
    fontStyle: 'normal',
    fontSize: '14px',
    lineHeight: '16px',
    [defaultTheme.breakpoints.up('sm')]: {
      fontStyle: 'normal',
      fontSize: '14px',
      lineHeight: '16px',
    },
  },
  subtitle2: {
    fontSize: '12px',
    lineHeight: '16px',
    [defaultTheme.breakpoints.up('sm')]: {
      fontStyle: 'normal',
      fontSize: '12px',
      lineHeight: '16px',
    },
  },
};

const paletteOptions: PaletteOptions | undefined = {
  primary: {
    main: '#1752AF',
  },
  text: {
    primary: '#39546A',
    secondary: '#60727E',
  },
};

const palette = createPalette(paletteOptions);

const borderRadius = 24;

export const Theme = createTheme({
  direction: 'rtl',
  shape: {
    borderRadius: borderRadius,
  },
  breakpoints: {},
  palette: palette,
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          direction: 'ltr',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          ':focus': {
            border: 'none',
            outline: '3px solid rgb(255, 140, 13)',
            span: {
              color: 'transparent',
            },
          },
          borderRadius: '32px',
          [`&.${buttonClasses.outlined}`]: {
            backgroundColor: palette.common.white,
          },
          [`&.${buttonClasses.contained}`]: {
            fontWeight: 'bold',
          },
          [`&.${buttonClasses.sizeLarge}`]: {
            minWidth: '100%',
            [defaultTheme.breakpoints.up('sm')]: {
              minWidth: '248px',
            },
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: palette.text.primary,
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          textDecoration: 'none',
          cursor: 'pointer',
        },
      },
    },
    MuiStack: {
      defaultProps: {
        width: '100%',
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: '#D1D6D9',
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          [`&.${accordionClasses.expanded}`]: {
            margin: '0',
          },
        },
      },
    },
    MuiAccordionSummary: {
      defaultProps: {
        expandIcon: <AccordionArrow />,
      },
    },
    MuiAvatar: {
      defaultProps: {
        variant: 'square',
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            '& ::placeholder': {
              color: '#39546A',
              opacity: 1,
            },
          },
        },
      },
    },
  },

  typography: typography,
});

export function RTL(props: PropsWithChildren<{}>) {
  return <CacheProvider value={cacheRtl}>{props.children}</CacheProvider>;
}
